/**
 * Checksum Utilities
 *
 * Provides functions for calculating and validating checksums
 * using the Web Crypto API.
 */

/**
 * Checks if Web Crypto API is available (requires secure context).
 */
function isCryptoAvailable(): boolean {
  return (
    typeof crypto !== 'undefined' &&
    typeof crypto.subtle !== 'undefined' &&
    typeof crypto.subtle.digest === 'function'
  );
}

/**
 * Calculates SHA-256 hash of a Blob.
 *
 * @param blob - The blob to hash
 * @returns Hex string of the SHA-256 hash
 */
export async function calculateSHA256(blob: Blob): Promise<string> {
  // Check if Web Crypto API is available (requires HTTPS or localhost)
  if (!isCryptoAvailable()) {
    console.warn(
      '[ChecksumUtils] ⚠️ Web Crypto API not available (requires HTTPS or localhost). Skipping checksum calculation.',
    );
    // Return a placeholder checksum to avoid breaking the flow
    return 'unavailable';
  }

  try {
    const buffer = await blob.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch (err) {
    console.error('[ChecksumUtils] Failed to calculate checksum:', err);
    // Return placeholder to avoid breaking the flow
    return 'error';
  }
}

/**
 * Result of a checksum validation, with diagnostic detail for the failure case.
 *
 * 2026-05-24 — Esse retorno foi expandido pra capturar expected/calculated
 * quando há mismatch. Em TV Samsung consumer (Tizen 7.7) observamos vídeos
 * indo pra status='error' sem visibilidade no overlay porque o caller original
 * só lia o booleano. Agora o caller pode logar o delta no `lastCacheError`.
 */
export interface ChecksumValidationResult {
  valid: boolean;
  reason?:
    | 'no-checksum'
    | 'crypto-unavailable'
    | 'calculation-failed'
    | 'mismatch';
  expected?: string;
  calculated?: string;
}

/**
 * Validates a blob against an expected checksum.
 *
 * @param blob - The blob to validate
 * @param expectedChecksum - The expected checksum (hex string)
 * @returns true if checksum matches (legacy boolean API)
 */
export async function validateChecksum(
  blob: Blob,
  expectedChecksum: string,
): Promise<boolean> {
  const result = await validateChecksumDetailed(blob, expectedChecksum);
  return result.valid;
}

/**
 * Validates a blob and returns detailed diagnostic info on failure.
 *
 * Use this version when you need to expose the expected vs calculated
 * checksum to the operator (e.g., debug overlay) — the boolean form discards
 * that detail and leaves you blind.
 */
export async function validateChecksumDetailed(
  blob: Blob,
  expectedChecksum: string,
): Promise<ChecksumValidationResult> {
  // Skip validation if no checksum provided
  if (!expectedChecksum || expectedChecksum === '') {
    console.log('[ChecksumUtils] No checksum provided, skipping validation');
    return { valid: true, reason: 'no-checksum' };
  }

  // 2026-05-24 — Normaliza prefixo "sha256:" (e variantes) que o backend
  // gera em conteúdos novos (post-FFmpeg fix). Conteúdos antigos no DB
  // estão como hex puro. Player calcula hex puro → comparar precisa
  // strip-ar o prefixo, senão TODO checksum prefixado falha (sintoma
  // observado em prod: 5 vídeos com "sha256:..." → 100% mismatch, mas
  // Heinneken sem prefixo bate certinho).
  // RFC 6920 + uso comum aceita também "SHA256:", "sha-256=", "sha-256:".
  const normalizedExpected = expectedChecksum
    .replace(/^sha-?256[:=]/i, '')
    .trim();

  try {
    const calculated = await calculateSHA256(blob);
    if (calculated === 'unavailable') {
      console.warn(
        '[ChecksumUtils] Web Crypto unavailable — accepting blob without validation',
      );
      return { valid: true, reason: 'crypto-unavailable' };
    }
    if (calculated === 'error') {
      console.warn(
        '[ChecksumUtils] SHA-256 calculation failed — accepting blob without validation',
      );
      return { valid: true, reason: 'calculation-failed' };
    }
    const isValid =
      calculated.toLowerCase() === normalizedExpected.toLowerCase();

    if (!isValid) {
      // Log truncated pra não poluir buffer — 16 chars é suficiente pra
      // identificar mismatch + investigação cruzada com server logs.
      console.warn('[ChecksumUtils] Checksum mismatch:', {
        expected: normalizedExpected.substring(0, 16) + '...',
        calculated: calculated.substring(0, 16) + '...',
      });
      return {
        valid: false,
        reason: 'mismatch',
        expected: normalizedExpected,
        calculated,
      };
    }

    return { valid: true };
  } catch (err) {
    console.error('[ChecksumUtils] Failed to calculate checksum:', err);
    // If we can't calculate, assume valid to not block playback
    return { valid: true, reason: 'calculation-failed' };
  }
}

/**
 * Calculates MD5 hash of a Blob.
 * Note: MD5 is not available in Web Crypto API, so this uses SHA-256
 * and is provided for API compatibility only.
 *
 * @param blob - The blob to hash
 * @returns Hex string of the hash
 */
export async function calculateMD5(blob: Blob): Promise<string> {
  // Web Crypto API doesn't support MD5, use SHA-256 instead
  console.warn('[ChecksumUtils] MD5 not supported, using SHA-256');
  return calculateSHA256(blob);
}

/**
 * Creates a checksum from a file URL (for generating test data).
 * This downloads the first few bytes and creates a partial hash.
 *
 * @param url - The URL to fetch
 * @returns Partial checksum string
 */
export async function generatePartialChecksum(url: string): Promise<string> {
  try {
    const response = await fetch(url, {
      headers: { Range: 'bytes=0-1023' }, // First 1KB
    });
    const blob = await response.blob();
    return calculateSHA256(blob);
  } catch (err) {
    console.error('[ChecksumUtils] Failed to generate checksum:', err);
    return '';
  }
}
